<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          We are required to identify whether the employer operates in an
          industry that may present increased risk.
        </p>
      </div>
    </help-drawer>

    <v-form name="PershingEmployerRisk" class="form" data-test-id="pershing-employer-risk-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id" />

        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="pershing-employer-risk-header">
              <span class="headerlight">
                Employer Industry <strong>Increased Risk</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>

        <!-- syncing message -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your information...</p>
        </div>

        <!-- data submitted -->
        <data-submitted-message
          v-if="isEmployerRiskComplete && formHidden && !isSyncing"
          :submitted="isEmployerRiskComplete"
          @hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />

        <!-- form -->
        <v-row v-if="!formHidden && !isSyncing" class="justify-center">
          <v-col class="step-form-container large">
            <label for="employerRisk" class="required" data-test-id="employer-risk-label">
              Employer Industry Increased Risk
            </label>
            <v-select
              v-model="form.employerIndustryIncreasedRisk"
              :items="riskOptions"
              :backgroundColor="inputBackgroundColor"
              outlined
              dense
              placeholder="Choose"
              hide-details="auto"
              @change="$v.form.employerIndustryIncreasedRisk.$touch()"
              :error-messages="employerRiskErrors"
              data-test-id="employer-risk-select"
              class="mb-4"
            />

            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-form>

    <bottom-nav @go-prev="prev" @go-next="next" data-test-id="pershing-employer-risk-nav" />
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import BottomNav from '@/components/common/BottomNav';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { MENUS } from '@/common/constants';
import { required } from 'vuelidate/lib/validators';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'PershingEmployerRisk',
  components: { BottomNav, DataSubmittedMessage, HelpDrawer, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    MENUS,
    form: {
      employerIndustryIncreasedRisk: null,
    },
    riskOptions: [
      'None (Not Applicable)',
      'Charities, Foundations',
      'Digital Assets',
      'Real Estate',
      'Shipping/ Freight/Transport',
    ],
    formHidden: true,
  }),
  validations() {
    return {
      form: {
        employerIndustryIncreasedRisk: { required },
      },
      validationGroup: ['form.employerIndustryIncreasedRisk'],
    };
  },
  computed: {
    helpTitle() {
      return 'Why do you need this?';
    },
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id);
      return page && page.status === 'IN_PROGRESS';
    },
    isEmployerRiskComplete() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id);
      return page && page.lastCompleted;
    },
    employerRiskErrors() {
      const errors = [];
      if (!this.$v.form.employerIndustryIncreasedRisk.$dirty) return errors;
      !this.$v.form.employerIndustryIncreasedRisk.required && errors.push('This field is required');
      return errors;
    },
  },
  methods: {
    next(route = null) {
      const to = route || this.pages[this.pageIndex + 1];
      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }
      const data = {
        employerIndustryIncreasedRisk: this.form.employerIndustryIncreasedRisk,
      };
      this.markPageAsSyncing(MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id);
      this.completeStep(MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id, data)
        .then(() => {
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },
    skip() {
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    toggleFormState(hide) {
      this.formHidden = hide;
      if (hide) {
        this.$v.$reset();
      }
    },
  },
  created() {
    // Ensure employment detail pages (including Pershing Employer Risk) are present
    this.interviewPages.addEmploymentPages();
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.findIndex(page => {
      if (typeof page === 'string') {
        return page === MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id;
      } else if (typeof page === 'object' && page.name) {
        return page.name === MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id;
      }
      return false;
    });
    this.formHidden = this.isEmployerRiskComplete;
  },
};
</script>

<style lang="scss" scoped>
.syncing-message {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}
.step-form-container.large {
  max-width: 800px;
}
.help-link {
  padding-top: 8px;
  a {
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      text-decoration: none;
    }
  }
}
.skip-for-now {
  margin-top: 20px;
  text-align: center;
  a {
    color: #666;
    text-decoration: underline;
    cursor: pointer;
    &:hover {
      text-decoration: none;
    }
  }
}
</style>
